// RD-UI-045 (KAN-689) — ONE memory store for the Team tab's "Last sign-in"
// column, so the admin dashboard can warm it at mount and the Team tab paints
// it with the rest of the row instead of popping it in a round-trip later.
// Pattern: mfaStatusPrefetch.js (in-flight dedupe, epoch guard).
//
// Source: `list_tenant_member_last_sign_in()` — admin/owner + 2FA gated and
// masked server-side. MEMORY ONLY: never written to storage. Keyed by the
// session's tenantId + userId (never a client-chosen value) and wiped at every
// sign-out via clearPerUserDeviceState / clearSignedOutMemoryState. A failure
// is silent (logError only): the last good map stays.
import { supabase } from '../../supabase';
import { logError } from '../error/errorHandler';

let snapshot = { key: null, map: null };
const inflight = new Map();
const settled = new Set(); // session keys whose load finished (success or error)
const rerun = new Set(); // keys that asked for FRESH data while a load was already in flight
const listeners = new Set();
let version = 0; // bumped on every notify — the useSyncExternalStore snapshot
let epoch = 0; // bumped by clearMemberLastSignInStore: a pre-sign-out request writes nothing

function storeKey(tenantId, userId) {
  return tenantId && userId ? `${tenantId}:${userId}` : null;
}

function notify() {
  version += 1;
  [...listeners].forEach((fn) => { try { fn(); } catch (_) { /* never break a reader */ } });
}

/** @returns {Map<string, string|null>|null} user_id -> ISO | null; `null` = not loaded for this session. */
export function peekMemberLastSignIn(tenantId, userId) {
  const key = storeKey(tenantId, userId);
  return key && snapshot.key === key ? snapshot.map : null;
}

/**
 * Fetch (deduped per session key). Never throws. `fresh` (a Team list reload)
 * joining an in-flight call schedules ONE trailing refetch, so a reload that
 * lands mid-request (e.g. an invite accepted) never keeps the older map.
 */
export function loadMemberLastSignIn(tenantId, userId, { fresh = false } = {}) {
  const key = storeKey(tenantId, userId);
  if (!key) return Promise.resolve(null);
  if (inflight.has(key)) {
    if (fresh) rerun.add(key);
    return inflight.get(key);
  }
  const startEpoch = epoch;
  const p = (async () => {
    try {
      const { data, error } = await supabase.rpc('list_tenant_member_last_sign_in');
      if (startEpoch !== epoch) return null; // signed out meanwhile — write nothing
      if (error) { logError('memberLastSignInStore', error); return peekMemberLastSignIn(tenantId, userId); }
      const next = new Map();
      (Array.isArray(data) ? data : []).forEach((r) => {
        if (r?.user_id) next.set(r.user_id, r.last_sign_in_at ?? null);
      });
      snapshot = { key, map: next };
      notify();
      return next;
    } catch (e) {
      if (startEpoch === epoch) logError('memberLastSignInStore', e);
      return null;
    } finally {
      if (startEpoch === epoch) {
        inflight.delete(key);
        if (!settled.has(key)) { settled.add(key); notify(); }
        if (rerun.delete(key)) void loadMemberLastSignIn(tenantId, userId);
      }
    }
  })();
  inflight.set(key, p);
  return p;
}

/** True once a load for this session finished (success OR error) — an error never leaves the cell blank. */
export function isMemberLastSignInSettled(tenantId, userId) {
  const key = storeKey(tenantId, userId);
  return Boolean(key && settled.has(key));
}

/** Store version — changes on every notify (useSyncExternalStore snapshot). */
export function getMemberLastSignInVersion() {
  return version;
}

/** True when both session keys are present (otherwise nothing can ever load). */
export function hasMemberLastSignInKeys(tenantId, userId) {
  return storeKey(tenantId, userId) != null;
}

export function subscribeMemberLastSignIn(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function clearMemberLastSignInStore() {
  epoch += 1;
  inflight.clear();
  settled.clear();
  rerun.clear();
  snapshot = { key: null, map: null };
  notify();
}
